import React, { PropsWithChildren } from 'react'
import { Container } from 'typescript-ioc/es5'
import { CheckCircleOutlined } from '@ant-design/icons'

import { BetterComponent } from '../better-component/better-component'
import { Translate } from '../translate/translate'
import { AppService, TranslationService } from '../../services/index'
// import './password-popover.scss'

import { AppState } from '../../stores/app'
import {
    PasswordValidators,
    REQUIRED_CHARACTER_CLASSES,
    valueMeetsCharacterReqs,
} from '../../_utils/password-validations'
import { Popover, Progress } from 'antd'

interface IProps {
    /**
     * Supplied by the enclosing Form.Item, which injects its control props into whatever
     * element it is given as a child. Both are forwarded to the wrapped input — without
     * that hand-off the input is unbound and the form never sees what was typed — and
     * `value` doubles as the source for the requirement checklist.
     */
    value?: string
    onChange?: (...args: any[]) => void
}

enum PasswordStrengthPct {
    VERY_WEAK = 0,
    WEAK = 33,
    REASONABLE = 66,
    STRONG = 88,
    VERY_STRONG = 100,
}

export class PasswordPopover extends BetterComponent<PropsWithChildren<IProps>, {}> {
    private appState: AppState
    private appService: AppService
    private translationService: TranslationService

    public constructor(props: IProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.translationService = Container.get(TranslationService)
    }

    public render() {
        const { children, ...controlProps } = this.props

        return (
            <Popover
                content={this.renderPopoverContent()}
                title="Your password must:"
                trigger="focus"
                placement="right"
                getPopupContainer={this.appService.getAppContainer}
            >
                {React.isValidElement(children)
                    ? React.cloneElement(children as React.ReactElement<any>, controlProps)
                    : children}
            </Popover>
        )
    }

    public renderPopoverContent() {
        const validIcon = <CheckCircleOutlined className="pw-rq-good" />
        const invalidIcon = <CheckCircleOutlined className="pw-rq-bad" />

        const password = this.props.value ?? ''
        const passwordEmpty = !password

        const validators = this.passwordValidators
        const characterReqsStatus = valueMeetsCharacterReqs(password, true)
        const requirementsMet = valueMeetsCharacterReqs(password)
        const validLength = !validators.tooShort(password)
        const notSameAsUserName = !validators.isSameAsUserName(password)
        const notSameAsUserEmail = !validators.isSameAsUserEmail(password)
        const score = validators.score(password)
        const strengthPct = this.calculatePercent(score)

        const charPhrase = {
            lowercase: 'Lower case letter (a-z)',
            uppercase: ' Upper case letters (A-Z)',
            number: 'Numbers (i.e. 0-9)',
            special: 'Special characters (e.g. !@#$%^&*)',
        }

        return (
            <div className="password-popover">
                <div>
                    {!passwordEmpty && validLength ? validIcon : invalidIcon}{' '}
                    <Translate name="common.passwordPopover.passwordLengthRequirement" />
                </div>
                <div>
                    {!passwordEmpty && notSameAsUserName ? validIcon : invalidIcon}{' '}
                    <Translate name="common.passwordPopover.passwordNotUserNameRequirement" />
                </div>
                <div>
                    {!passwordEmpty && notSameAsUserEmail ? validIcon : invalidIcon}{' '}
                    <Translate name="common.passwordPopover.passwordNotUserEmailRequirement" />
                </div>
                <div>
                    {!passwordEmpty && requirementsMet ? validIcon : invalidIcon}{' '}
                    <span>
                        Contains at least {REQUIRED_CHARACTER_CLASSES} of the 4 following character requirements:
                    </span>
                    <ul>
                        {Object.keys(charPhrase).map((val: string, idx) => (
                            <li key={idx}>
                                {!passwordEmpty && characterReqsStatus?.[val] ? validIcon : invalidIcon}{' '}
                                {charPhrase[val]}
                            </li>
                        ))}
                    </ul>
                </div>
                {!passwordEmpty && strengthPct >= PasswordStrengthPct.REASONABLE ? validIcon : invalidIcon}{' '}
                <Translate name="common.passwordPopover.passwordBeStrong" />
                <div>
                    <Progress percent={passwordEmpty ? 0 : strengthPct} size="small" showInfo={false} />
                    {!passwordEmpty ? `Your password is ${score}` : null}
                </div>
            </div>
        )
    }

    private calculatePercent(score) {
        let percent = PasswordStrengthPct.VERY_WEAK

        switch (score) {
            case 'very weak':
                percent = PasswordStrengthPct.VERY_WEAK
                break
            case 'weak':
                percent = PasswordStrengthPct.WEAK
                break
            case 'medium':
                percent = PasswordStrengthPct.REASONABLE
                break
            case 'strong':
                percent = PasswordStrengthPct.STRONG
                break
            case 'very strong':
                percent = PasswordStrengthPct.VERY_STRONG
                break
        }

        return percent
    }

    private get passwordValidators() {
        return new PasswordValidators(this.appState.currentUser!)
    }
}
